import { fetchSolutionDetails } from '@gn-audio/core/api/compatibility-guide/fetch-solution-details';
import { ICgGroup, IManufacturer, IManufacturerDevice, ISolutionBundleItem, ISolutionDetails } from '@gn-audio/core/api/compatibility-guide/models';
import { fetchGroupPrice } from '@gn-audio/core/api/pricing/fetch-group-price';
import { IFromPrice, ISFCCGroupPrice } from '@gn-audio/core/api/pricing/models';
import { getAvailabilityState } from '@gn-audio/core/api/pricing/utilities';
import { IGroup } from '@gn-audio/core/api/product-catalogue/models/group';
import { fetchEcomSetting } from '@gn-audio/core/api/site-settings/fetch-ecom-setting';
import { IEcomSetting } from "@gn-audio/core/api/site-settings/models/ecom-setting";
import { AvailabilityState } from '@gn-audio/core/common/enums';
import { Component, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from 'angular-ts-decorators';
import { ITranslationService } from 'src/Foundation/Translation/platform';

import { IFilter } from '../models/filter';

import template from './cg-customizer-accessories.component.html';

@Component({
  selector: 'cgCustomizerAccessories',
  template,
})
export class CgCustomizerAccessoriesComponent implements OnInit, OnChanges {
  public static $inject: ReadonlyArray<string> = ['$q', 'translation'];

  @Input() public filter?: IFilter;
  @Input() public group?: IGroup;
  @Input() public selectedBundles?: ISolutionBundleItem[];

  @Output() public onBundleExclude?: (args: { bundle: ISolutionBundleItem; }) => void;
  @Output() public onBundleInclude?: (args: { bundle: ISolutionBundleItem; }) => void;
  @Output() public onBundleReset?: () => void;
  @Output() public onSkip?: (args: { filter: IFilter; }) => void;

  public accessories: string;
  public bundlePrices: (IFromPrice | ISFCCGroupPrice)[] = [];
  public bundles: ISolutionBundleItem[] = [];
  public ecomEnabled = false;
  public loading = false;
  public manufacturer: IManufacturer = null;
  public model: IManufacturerDevice = null;
  public nonDirectBundles: ISolutionBundleItem[] = [];
  public notCompatible = false;
  public groupState: AvailabilityState = AvailabilityState.WhereToBuy;

  constructor(
    @Inject('$q') private $q: ng.IQService,
    @Inject('translation') private translation: ITranslationService,
  ) { }

  public ngOnInit(): void {
    this.$q.when(fetchEcomSetting())
      .then((setting: IEcomSetting | null) => { this.ecomEnabled = !!setting?.ecomEnabled; });
  }

  public ngOnChanges(onChangesObject: SimpleChanges<CgCustomizerAccessoriesComponent>): void {
    if (onChangesObject.selectedBundles !== undefined) {
      this.bundles.forEach((b: ISolutionBundleItem): void => {
        b.included = this.selectedBundles.some((sb: ISolutionBundleItem): boolean => sb.bundleHash === b.bundleHash);
      });
    }

    if (onChangesObject.group !== undefined) {
      this.reset();

      if (this.group === undefined) {
        return;
      }

      this.$q.when(fetchEcomSetting())
        .then((ecomSetting: IEcomSetting | null) => {
          if (!ecomSetting?.ecomEnabled) {
            return;
          }

          this.$q.when(fetchGroupPrice(this.group.groupId))
            .then((price: IFromPrice | ISFCCGroupPrice | null) => {
              this.groupState = getAvailabilityState(price);
            });
        });
    }
  }

  public groupAvailable(): boolean {
    return this.groupState < AvailabilityState.WhereToBuy;
  }

  public onManufacturerSelected(manufacturer: IManufacturer): void {
    this.reset();
    this.manufacturer = manufacturer;
  }

  public onModelSelected(model: IManufacturerDevice): void {
    this.reset(false);
    this.model = model;
    if (this.model === null) {
      return;
    }

    this.loading = true;
    this.$q.when(fetchSolutionDetails([this.model.searchKey], this.group.groupId))
      .then(
        (solutions: ISolutionDetails): void => {
          this.bundles = solutions.thirdPartyProducts[0].bundles;
          this.nonDirectBundles = this.bundles.filter((b: ISolutionBundleItem): boolean => b.bundle.length > 0);
          this.accessories = solutions.group.accessories;
        },
        (): void => {
          this.notCompatible = true;
        },
      )
      .finally((): void => {
        this.loading = false;
      });
  }

  public toggleBundle(bundle: ISolutionBundleItem): void {
    bundle.included = !Boolean(bundle.included);
    if (bundle.included) {
      if (this.onBundleInclude) {
        this.onBundleInclude({ bundle });
      }
    } else {
      if (this.onBundleExclude) {
        this.onBundleExclude({ bundle });
      }
    }
  }

  public getMessage(): string {
    let dictionaryKey = '';
    let dictionaryValue = '';

    if (this.notCompatible) {
      dictionaryKey = 'CgCustomizerNotCompatible';
    } else if (this.hasDirectConnection()) {
      dictionaryKey = 'CgCustomizerDirectConnection';
    } else if (this.isRequired()) {
      dictionaryKey = 'CgCustomizerRequiredAccessories';
    } else if (this.isRecommended()) {
      dictionaryKey = 'CgCustomizerRecommendedAccessories';
    }

    if (dictionaryKey === '') {
      return '';
    }

    if (this.manufacturer === null || this.model === null) {
      return '';
    }

    dictionaryValue = this.translation
      .getSystem(dictionaryKey)
      .replace('{manufacturer}', this.manufacturer.name)
      .replace('{model}', this.model.name);

    if (this.group !== undefined) {
      dictionaryValue = dictionaryValue.replace('{product}', this.group.productName);
    }

    return dictionaryValue;
  }

  public hasNonDirectBundles(): boolean {
    return this.nonDirectBundles.length > 0;
  }

  public hasDirectConnection(): boolean {
    return this.hasBundles() && this.bundles.some((b: ISolutionBundleItem): boolean => b.bundle.length === 0);
  }

  public isRecommended(): boolean {
    return this.hasBundles() && this.accessories === 'Recommended';
  }

  public isRequired(): boolean {
    return this.hasBundles() && this.accessories === 'Required';
  }

  public hasBundles(): boolean {
    return this.bundles.length > 0;
  }

  public onPriceLoaded(price: IFromPrice | ISFCCGroupPrice): void {
    if (!price) {
      return;
    }

    this.bundlePrices.push(price);
  }

  public isBundleAvailable(bundle: ISolutionBundleItem): boolean {
    const bundleAvailable: boolean = bundle.bundle
      .every((g: ICgGroup): boolean => {
        const bundleItemPrice: IFromPrice | ISFCCGroupPrice | undefined = this.bundlePrices
          .find((p: IFromPrice | ISFCCGroupPrice): boolean => p.sku === g.sku);

        return !!bundleItemPrice && bundleItemPrice.availabilityState < AvailabilityState.WhereToBuy;
      });

    return this.ecomEnabled && this.groupAvailable() && bundleAvailable;
  }

  public skip(): void {
    this.reset();
    this.filter.value = 'skipped';

    if (this.onSkip) {
      this.onSkip({ filter: this.filter });
    }
  }

  private reset(resetManufacturer = true): void {
    if (resetManufacturer) {
      this.manufacturer = null;
    }

    this.model = null;
    this.bundles = [];
    this.nonDirectBundles = [];
    this.bundlePrices = [];
    this.notCompatible = false;

    if (this.onBundleReset) {
      this.onBundleReset();
    }
  }
}
